import React, { useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Popconfirm,
  Modal,
  Form,
  Input,
  Card,
  Breadcrumb,
  Typography,
  Row,
  Col,
  Tag,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CarOutlined,
  AppstoreOutlined,
  BarsOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useVehicleManagement } from '../hooks/useVehicleManagement';
import { MakeSummary, ModelSummary } from '../../api/services/vehicleManagementService';

const { Text } = Typography;

const MakesTable: React.FC<{
  makes: MakeSummary[];
  loading: boolean;
  actionLoading: boolean;
  onDrill: (make: string) => void;
  onDelete: (make: string) => void;
  onAdd: () => void;
  onRefresh: () => void;
}> = ({ makes, loading, actionLoading, onDrill, onDelete, onAdd, onRefresh }) => {
  const { t } = useTranslation();
  const columns = [
    {
      title: t('admin.vehicle_mgmt.col_make'),
      dataIndex: 'make',
      key: 'make',
      render: (make: string) => (
        <Button
          type="link"
          onClick={() => onDrill(make)}
          style={{ padding: 0, fontWeight: 600, color: 'var(--admin-primary)' }}
          icon={<CarOutlined />}
        >
          {' '}{make}
        </Button>
      ),
    },
    {
      title: t('admin.vehicle_mgmt.col_models'),
      dataIndex: 'modelCount',
      key: 'modelCount',
      width: 110,
      render: (n: number) => <Tag color="blue">{t('admin.vehicle_mgmt.tag_models_count', { count: n })}</Tag>,
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_: any, row: MakeSummary) => (
        <Space>
          <Tooltip title={t('admin.vehicle_mgmt.tooltip_browse_models')}>
            <Button
              size="small"
              onClick={() => onDrill(row.make)}
              style={{ borderRadius: 6 }}
            >
              {t('admin.vehicle_mgmt.btn_models')}
            </Button>
          </Tooltip>
          <Popconfirm
            title={t('admin.vehicle_mgmt.delete_make_title', { name: row.make })}
            description={t('admin.vehicle_mgmt.delete_make_desc')}
            okText={t('admin.vehicle_mgmt.delete_btn')}
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(row.make)}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              loading={actionLoading}
              style={{ borderRadius: 6 }}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <AppstoreOutlined />
          <Text strong style={{ fontSize: 15 }}>{t('admin.vehicle_mgmt.section_makes_title')}</Text>
          <Tag color="default">{makes.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={onRefresh} loading={loading} style={{ borderRadius: 8 }}>
            {t('admin.vehicle_mgmt.btn_refresh')}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={onAdd} style={{ borderRadius: 8, background: 'var(--admin-primary)', borderColor: 'var(--admin-primary)' }}>
            {t('admin.vehicle_mgmt.btn_add_make')}
          </Button>
        </Space>
      }
      className="admin-table-card"
      bordered={false}
    >
      <Table
        rowKey="make"
        dataSource={makes}
        columns={columns}
        loading={loading}
        className="admin-table"
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
        scroll={{ x: true }}
      />
    </Card>
  );
};

const ModelsTable: React.FC<{
  make: string;
  models: ModelSummary[];
  loading: boolean;
  actionLoading: boolean;
  onDelete: (model: string) => void;
  onAdd: () => void;
  onRefresh: () => void;
}> = ({ make, models, loading, actionLoading, onDelete, onAdd, onRefresh }) => {
  const { t } = useTranslation();
  const columns = [
    {
      title: t('admin.vehicle_mgmt.col_model'),
      dataIndex: 'model',
      key: 'model',
      render: (model: string) => (
        <Text strong style={{ color: 'var(--admin-primary)' }}>
          <BarsOutlined style={{ marginRight: 6 }} />{model}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_: any, row: ModelSummary) => (
        <Popconfirm
          title={t('admin.vehicle_mgmt.delete_model_title', { name: row.model })}
          okText={t('admin.vehicle_mgmt.delete_btn')}
          okButtonProps={{ danger: true }}
          onConfirm={() => onDelete(row.model)}
        >
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            loading={actionLoading}
            style={{ borderRadius: 6 }}
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Card
      title={
        <Space>
          <BarsOutlined />
          <Text strong style={{ fontSize: 15 }}>{t('admin.vehicle_mgmt.models_section_title', { make })}</Text>
          <Tag color="default">{models.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={onRefresh} loading={loading} style={{ borderRadius: 8 }}>
            {t('admin.vehicle_mgmt.btn_refresh')}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={onAdd} style={{ borderRadius: 8, background: 'var(--admin-primary)', borderColor: 'var(--admin-primary)' }}>
            {t('admin.vehicle_mgmt.btn_add_model')}
          </Button>
        </Space>
      }
      className="admin-table-card"
      bordered={false}
    >
      <Table
        rowKey="model"
        dataSource={models}
        columns={columns}
        loading={loading}
        className="admin-table"
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
        scroll={{ x: true }}
      />
    </Card>
  );
};

const VehicleManagement: React.FC = () => {
  const { t } = useTranslation();
  const {
    makes, models,
    selectedMake, activeLevel,
    makesLoading, modelsLoading, actionLoading,
    addMakeOpen, setAddMakeOpen,
    addModelOpen, setAddModelOpen,
    fetchMakes, fetchModels,
    drillIntoMake, goBack,
    handleCreateMake, handleDeleteMake,
    handleCreateModel, handleDeleteModel,
  } = useVehicleManagement();

  const [makeForm] = Form.useForm();
  const [modelForm] = Form.useForm();

  useEffect(() => {
    fetchMakes();
  }, [fetchMakes]);

  const breadcrumbItems = [
    {
      title: (
        <Button
          type="link"
          style={{ padding: 0, color: activeLevel === 'makes' ? 'inherit' : 'var(--admin-primary)', fontWeight: activeLevel === 'makes' ? 700 : 400 }}
          onClick={() => { if (activeLevel !== 'makes') goBack(); }}
        >
          {t('admin.vehicle_mgmt.breadcrumb_makes')}
        </Button>
      ),
    },
    ...(selectedMake ? [{ title: <Text strong>{selectedMake}</Text> }] : []),
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Row gutter={16}>
        {[
          { label: t('admin.vehicle_mgmt.stat_total_makes'), value: makes.length, color: 'var(--admin-primary)' },
          { label: t('admin.vehicle_mgmt.stat_total_models'), value: makes.reduce((s, m) => s + m.modelCount, 0).toLocaleString(), color: '#0284c7' },
        ].map(({ label, value, color }) => (
          <Col xs={24} sm={12} key={label}>
            <div style={{
              background: 'var(--admin-surface)',
              border: '1px solid var(--admin-border)',
              borderRadius: 12,
              boxShadow: 'var(--admin-shadow)',
              padding: '28px 32px',
            }}>
              <div style={{ fontSize: 12, color: 'var(--admin-text-secondary)', marginBottom: 10 }}>{label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, lineHeight: 1, color }}>{value}</div>
            </div>
          </Col>
        ))}
      </Row>

      {activeLevel !== 'makes' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button onClick={goBack} style={{ borderRadius: 8 }}>{t('admin.vehicle_mgmt.back')}</Button>
          <Breadcrumb items={breadcrumbItems} />
        </div>
      )}

      {activeLevel === 'makes' && (
        <MakesTable
          makes={makes}
          loading={makesLoading}
          actionLoading={actionLoading}
          onDrill={drillIntoMake}
          onDelete={handleDeleteMake}
          onAdd={() => { makeForm.resetFields(); setAddMakeOpen(true); }}
          onRefresh={fetchMakes}
        />
      )}

      {activeLevel === 'models' && selectedMake && (
        <ModelsTable
          make={selectedMake}
          models={models}
          loading={modelsLoading}
          actionLoading={actionLoading}
          onDelete={handleDeleteModel}
          onAdd={() => { modelForm.resetFields(); setAddModelOpen(true); }}
          onRefresh={() => fetchModels(selectedMake)}
        />
      )}

      <Modal
        title={t('admin.vehicle_mgmt.modal_add_make')}
        open={addMakeOpen}
        onOk={async () => {
          try {
            const { name } = await makeForm.validateFields();
            handleCreateMake(name);
          } catch { /* validation */ }
        }}
        onCancel={() => setAddMakeOpen(false)}
        okText={t('admin.vehicle_mgmt.modal_create_make')}
        confirmLoading={actionLoading}
        okButtonProps={{ style: { background: 'var(--admin-primary)', borderColor: 'var(--admin-primary)', borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
      >
        <Form form={makeForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item
            name="name"
            label={t('admin.vehicle_mgmt.label_make_name')}
            rules={[
              { required: true, message: t('admin.vehicle_mgmt.validation_name_required') },
              { max: 100, message: t('admin.vehicle_mgmt.validation_max_100') },
            ]}
          >
            <Input placeholder={t('admin.vehicle_mgmt.ph_make_name')} style={{ borderRadius: 8 }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('admin.vehicle_mgmt.modal_add_model', { make: selectedMake ?? '' })}
        open={addModelOpen}
        onOk={async () => {
          try {
            const { model } = await modelForm.validateFields();
            handleCreateModel(model);
          } catch { /* validation */ }
        }}
        onCancel={() => setAddModelOpen(false)}
        okText={t('admin.vehicle_mgmt.modal_create_model')}
        confirmLoading={actionLoading}
        okButtonProps={{ style: { background: 'var(--admin-primary)', borderColor: 'var(--admin-primary)', borderRadius: 8 } }}
        cancelButtonProps={{ style: { borderRadius: 8 } }}
      >
        <Form form={modelForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item
            name="model"
            label={t('admin.vehicle_mgmt.label_model_name')}
            rules={[
              { required: true, message: t('admin.vehicle_mgmt.validation_name_required') },
              { max: 150, message: t('admin.vehicle_mgmt.validation_max_150') },
            ]}
          >
            <Input placeholder={t('admin.vehicle_mgmt.ph_model_name')} style={{ borderRadius: 8 }} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default VehicleManagement;
